import { departmentsQuery } from "@common/api/departments"
import { quoteTaskCommentCountsQuery } from "@common/api/quoting/comment"
import type {
  Currency,
  DepartmentPublic,
  QuoteDetailPublic,
  QuoteTaskDetailPublic,
  QuoteTaskGroupPublic,
  QuoteTaskStatus,
  UpdateQuote,
} from "@common/client"
import { MultiSelectBody } from "@common/components/shared/multi-select-body"
import { Badge } from "@common/components/ui/badge"
import { Button } from "@common/components/ui/button"
import { Checkbox } from "@common/components/ui/checkbox"
import { GridTable } from "@common/components/ui/grid-table"
import { Input } from "@common/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@common/components/ui/popover"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useRangeToggle } from "@common/hooks/use-range-toggle"
import { chapterText } from "@common/lib/ata-chapter"
import { formatShortDate } from "@common/lib/date-utils"
import { formatHours } from "@common/lib/number"
import { cn } from "@common/lib/utils"
import { useQuery } from "@tanstack/react-query"
import {
  getRouteApi,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useEffect, useState } from "react"
import {
  FloatingAction,
  FloatingActionBar,
} from "@/components/layout/floating-action-bar"
import { GroupedTable } from "@/components/layout/grouped-table"
import { ListEmpty } from "@/components/layout/list-empty"
import {
  type ListColumns,
  ListTableCell,
  ListTableCheckbox,
  ListTableHeader,
  ListTableRow,
  ListTableSort,
  ListTableText,
} from "@/components/layout/list-table"
import { type ColumnArrange, useColumnLayout } from "@/hooks/use-column-layout"
import type { ColumnSizing } from "@/lib/column-widths"
import { phaseGroups, rephased } from "@/lib/phases"
import { formatTotals } from "@/routes/_layout/quoting_/quotes/-lib/charge-math"
import {
  acrossTasks,
  underTask,
} from "@/routes/_layout/quoting_/quotes/-lib/ops"
import {
  partCharges,
  totalCharges,
} from "@/routes/_layout/quoting_/quotes/builder/$quoteId/-common/lib/task-form"
import AddCircleOutline from "~icons/material-symbols/add-circle-outline"
import Block from "~icons/material-symbols/block"
import ChatBubbleOutline from "~icons/material-symbols/chat-bubble-outline"
import Check from "~icons/material-symbols/check"
import Checklist from "~icons/material-symbols/checklist"
import DoNotDisturbOnOutline from "~icons/material-symbols/do-not-disturb-on-outline"
import ExpandMore from "~icons/material-symbols/expand-more"
import FolderOutline from "~icons/material-symbols/folder-outline"
import LayersOutline from "~icons/material-symbols/layers-outline"
import RadioButtonChecked from "~icons/material-symbols/radio-button-checked"
import { dueOutlookLabel } from "../-lib/due"
import { useEditable } from "../-lib/editable"
import type { TaskFilterValues } from "../-lib/filters"
import {
  billingLabel,
  billingOf,
  COLUMN_LABELS,
  type Column,
  filtered,
  type ItemSort,
  type ItemsDisplay,
  itemGroup,
  sorted,
} from "../-lib/items"
import { labourCharges, labourHours } from "../-lib/labour"
import { isPartsQuote, taskTitle } from "../-lib/parts-quote"
import { usePhasesEnabled } from "../-lib/phases"
import { inQueue } from "../-lib/review-flow"
import { useReviewTasks } from "../-lib/reviewing"
import { useTaskGroups } from "../-lib/task-groups"
import {
  acceptedStatus,
  chargesPrice,
  includedStatus,
  isComplete,
  lastUpdated,
  pricedParts,
  serviceCharges,
  taskTotal,
} from "../-lib/task-values"
import { travellerOf } from "../-lib/traveller"
import { useQuoteUpdate } from "../-lib/update"
import { PhaseGroupActions } from "./phase-actions"
import { TaskStatusIcon } from "./task-status-icon"

const quoteRoute = getRouteApi("/_layout/quotes_/$quoteId")

const ROW_ACTIONS = cn(
  "absolute top-1/2 right-24 flex -translate-y-1/2 gap-0.5 rounded-md bg-background p-0.5 shadow-sm",
  "opacity-0 transition-opacity focus-within:opacity-100 group-hover/row:opacity-100",
)

type ItemSlot = "select" | "task" | "status" | "description" | Column

const SORT_LABELS: Record<ItemSort, string> = {
  task: "Task",
  description: "Description",
  total: "Total",
  updated: "Updated",
}

export const ITEM_SIZING: ColumnSizing<ItemSlot> = {
  id: "quote-items",
  widths: {
    select: 2.25,
    task: 7,
    status: 2,
    description: 10,
    ata: 4,
    task_group: 9,
    department: 8,
    billing: 8,
    hours: 5,
    labour_price: 9,
    parts: 4.5,
    parts_price: 9,
    services: 5,
    services_price: 9,
    total: 9,
    comments: 6.5,
    updated: 5,
  },
  fill: "description",
  pinned: ["select", "status"],
  extras: ["select", "task", "status", "description"],
  dropOrder: [
    "updated",
    "billing",
    "department",
    "task_group",
    "ata",
    "parts",
    "services",
    "labour_price",
    "services_price",
    "parts_price",
    "hours",
    "comments",
    "total",
  ],
}

const ITEM_COLUMNS: Omit<ListColumns<ItemSlot>, "slots"> = {
  end: [
    "hours",
    "labour_price",
    "parts",
    "parts_price",
    "services",
    "services_price",
    "total",
    "comments",
  ],
  muted: ["task", "updated"],
}

export function ItemsTable({
  quote,
  display,
  filters,
  shown,
  onClear,
}: {
  quote: QuoteDetailPublic
  display: ItemsDisplay
  filters: TaskFilterValues
  shown: Column[]
  onClear: () => void
}): React.ReactElement {
  const taskGroups = useTaskGroups()
  const { data: departments } = useQuery({
    ...departmentsQuery(),
    enabled: shown.includes("department"),
  })
  const { data: commentCounts } = useQuery({
    ...quoteTaskCommentCountsQuery({ quote_id: quote.id }),
    enabled: shown.includes("comments"),
  })
  const commentsByTask = new Map(
    commentCounts?.map((count): [string, number] => [
      count.quote_task_id,
      count.count,
    ]),
  )
  const reviewer = useHasPermission("quote:review")
  const { sort, dir } = quoteRoute.useSearch()
  const params = useParams({ strict: false })
  const selectedId = "taskId" in params ? params.taskId : undefined
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const tasks = sorted(
    quote,
    filtered(quote.active_tasks, filters),
    sort,
    dir ?? "asc",
  )
  const chosen = tasks.filter((task) => picked.has(task.id))
  const layout = useColumnLayout<ItemSlot>(
    ["select", "task", "status", "description", ...shown],
    ITEM_SIZING,
  )
  const columns = { ...ITEM_COLUMNS, slots: layout.slots }
  const style = { gridTemplateColumns: layout.template }
  const rangeToggle = useRangeToggle<string>()

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        setPicked(new Set())
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const showsEmptyPhases =
    display.group === "phase" && quote.active_phases.length > 0
  if (quote.active_tasks.length === 0 && !showsEmptyPhases) {
    return (
      <ListEmpty
        icon={Checklist}
        title="No tasks yet"
        body="Add a task to start pricing this quote."
      />
    )
  }
  if (tasks.length === 0) {
    return (
      <ListEmpty
        icon={Checklist}
        title="No matching tasks"
        body="Try a different filter."
        onClear={onClear}
      />
    )
  }

  const toggle = (
    order: QuoteTaskDetailPublic[],
    taskId: string,
    range: boolean,
  ) => {
    const ids = order.map((task) => task.id)
    setPicked(rangeToggle(ids, picked, taskId, range))
  }

  const header = (
    <ItemsHeader
      columns={columns}
      arrange={layout.arrange}
      all={tasks.length > 0 && chosen.length === tasks.length}
      onAll={(on) => setPicked(new Set(on ? tasks.map((task) => task.id) : []))}
    />
  )
  const renderRows = (
    rows: QuoteTaskDetailPublic[],
    order: QuoteTaskDetailPublic[],
  ) => (
    <>
      {rows.map((task) => (
        <ItemRow
          key={task.id}
          quote={quote}
          columns={columns}
          task={task}
          taskGroups={taskGroups}
          departments={departments?.data}
          comments={commentsByTask.get(task.id) ?? 0}
          selected={task.id === selectedId}
          checked={picked.has(task.id)}
          reviewing={reviewer && inQueue(quote, task)}
          onCheck={(range) => toggle(order, task.id, range)}
        />
      ))}
    </>
  )

  return (
    <>
      {display.group ? (
        <GroupedTable
          rows={tasks}
          style={style}
          header={header}
          group={display.group}
          subgroup={display.subgroup}
          groupOf={(task, grouping) =>
            itemGroup(quote, task, grouping, taskGroups)
          }
          emptyGroups={(grouping) =>
            grouping === "phase" ? phaseGroups(quote.active_phases) : []
          }
          actions={(_, section) =>
            display.group === "phase" && (
              <PhaseGroupActions quote={quote} section={section} />
            )
          }
          renderRows={renderRows}
          sticky
        />
      ) : (
        <GridTable style={style}>
          {header}
          {renderRows(tasks, tasks)}
        </GridTable>
      )}
      {chosen.length > 0 && (
        <SelectionBar
          quote={quote}
          tasks={chosen}
          onClear={() => setPicked(new Set())}
        />
      )}
    </>
  )
}

function ItemsHeader({
  columns,
  arrange,
  all,
  onAll,
}: {
  columns: ListColumns<ItemSlot>
  arrange: ColumnArrange<ItemSlot>
  all: boolean
  onAll: (on: boolean) => void
}): React.ReactElement {
  const { sort, dir } = quoteRoute.useSearch()
  const navigate = useNavigate()
  const current = sort ? { column: sort, direction: dir ?? "asc" } : undefined

  const order = (key: ItemSort) =>
    navigate({
      to: ".",
      search: (previous) => ({
        ...previous,
        sort: key,
        dir: sort === key && dir !== "desc" ? "desc" : "asc",
      }),
      replace: true,
    })

  return (
    <ListTableHeader columns={columns} sort={current} arrange={arrange}>
      {(slot) => {
        switch (slot) {
          case "select":
            return (
              <Checkbox
                aria-label="Select all tasks"
                checked={all}
                onCheckedChange={(next) => onAll(next === true)}
              />
            )
          case "status":
            return null
          case "ata":
          case "task_group":
          case "department":
          case "billing":
          case "hours":
          case "labour_price":
          case "parts":
          case "parts_price":
          case "services":
          case "services_price":
          case "comments":
            return COLUMN_LABELS[slot]
          default:
            return (
              <ListTableSort
                column={slot}
                label={SORT_LABELS[slot]}
                sort={current}
                onSort={order}
              />
            )
        }
      }}
    </ListTableHeader>
  )
}

function ItemRow({
  quote,
  columns,
  task,
  taskGroups,
  departments,
  comments,
  selected,
  checked,
  reviewing,
  onCheck,
}: {
  quote: QuoteDetailPublic
  columns: ListColumns<ItemSlot>
  task: QuoteTaskDetailPublic
  taskGroups: QuoteTaskGroupPublic[]
  departments: DepartmentPublic[] | undefined
  comments: number
  selected: boolean
  checked: boolean
  reviewing: boolean
  onCheck: (range: boolean) => void
}): React.ReactElement {
  const currency = quote.currency
  const unquoted = task.status === "excluded" || task.status === "rejected"

  return (
    <ListTableRow aria-selected={selected} selected={selected || checked}>
      <ListTableCell columns={columns} slot="select">
        <ListTableCheckbox
          label={`Select ${task.number}`}
          checked={checked}
          onToggle={onCheck}
        />
      </ListTableCell>
      <Link
        to={selected ? "/quotes/$quoteId" : "/quotes/$quoteId/tasks/$taskId"}
        params={{ quoteId: quote.id, taskId: task.id }}
        search={true}
        className="col-start-2 col-end-[-1] grid grid-cols-subgrid text-foreground hover:no-underline"
      >
        {columns.slots
          .filter((slot) => slot !== "select")
          .map((slot) => (
            <ListTableCell key={slot} columns={columns} slot={slot}>
              <ItemValue
                slot={slot}
                quote={quote}
                task={task}
                taskGroups={taskGroups}
                departments={departments}
                comments={comments}
                unquoted={unquoted}
                currency={currency}
              />
            </ListTableCell>
          ))}
      </Link>
      {reviewing && <ReviewRowActions quote={quote} task={task} />}
    </ListTableRow>
  )
}

function ItemValue({
  slot,
  quote,
  task,
  taskGroups,
  departments,
  comments,
  unquoted,
  currency,
}: {
  slot: ItemSlot
  quote: QuoteDetailPublic
  task: QuoteTaskDetailPublic
  taskGroups: QuoteTaskGroupPublic[]
  departments: DepartmentPublic[] | undefined
  comments: number
  unquoted: boolean
  currency: Currency
}): React.ReactNode {
  switch (slot) {
    case "select":
      return null
    case "task":
      return <ListTableText>{task.number}</ListTableText>
    case "status":
      return (
        <TaskStatusIcon status={task.status} disposition={quote.disposition} />
      )
    case "description":
      return <DescriptionValue quote={quote} task={task} unquoted={unquoted} />
    case "ata":
      return (
        task.ata_chapter !== null && (
          <ListTableText>{chapterText(task.ata_chapter)}</ListTableText>
        )
      )
    case "task_group":
      return (
        <ListTableText>
          {taskGroups.find((group) => group.id === task.task_group_id)?.name}
        </ListTableText>
      )
    case "department": {
      const departmentId = task.department_id ?? quote.department_id
      if (departmentId === null || departments === undefined) return null
      const department = departments.find(({ id }) => id === departmentId)
      return (
        <ListTableText>
          {department?.name ?? "Archived department"}
        </ListTableText>
      )
    }
    case "billing": {
      const billing = billingOf(quote, task)
      return (
        billing !== "unset" && (
          <ListTableText>{billingLabel(billing)}</ListTableText>
        )
      )
    }
    case "hours": {
      const hours = labourHours(task)
      return (
        hours !== null && (
          <ListTableText struck={unquoted}>{formatHours(hours)}</ListTableText>
        )
      )
    }
    case "labour_price":
      return (
        <ListTableText struck={unquoted}>
          {chargesPrice(labourCharges(task), currency)}
        </ListTableText>
      )
    case "parts": {
      const count = partCharges(task).length
      return count > 0 && <ListTableText>{count}</ListTableText>
    }
    case "parts_price":
      return (
        <ListTableText struck={unquoted}>
          {chargesPrice(pricedParts(task), currency)}
        </ListTableText>
      )
    case "services": {
      const count = serviceCharges(task).length
      return count > 0 && <ListTableText>{count}</ListTableText>
    }
    case "services_price":
      return (
        <ListTableText struck={unquoted}>
          {chargesPrice(serviceCharges(task), currency)}
        </ListTableText>
      )
    case "total":
      return (
        <ListTableText struck={unquoted}>
          {taskTotal(quote, task, currency)}
        </ListTableText>
      )
    case "comments":
      return comments > 0 && <ListTableText>{comments}</ListTableText>
    case "updated":
      return <ListTableText>{formatShortDate(lastUpdated(task))}</ListTableText>
  }
}

function DescriptionValue({
  quote,
  task,
  unquoted,
}: {
  quote: QuoteDetailPublic
  task: QuoteTaskDetailPublic
  unquoted: boolean
}): React.ReactElement {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <ListTableText struck={unquoted}>{taskTitle(quote, task)}</ListTableText>
      {task.due_outlook === "beyond_horizon" && !unquoted && (
        <Badge
          variant="outline"
          className="shrink-0 text-muted-foreground"
          title={dueOutlookLabel(quote, "beyond_horizon")}
        >
          Out of window
        </Badge>
      )}
    </span>
  )
}

function SelectionBar({
  quote,
  tasks,
  onClear,
}: {
  quote: QuoteDetailPublic
  tasks: QuoteTaskDetailPublic[]
  onClear: () => void
}): React.ReactElement {
  const editable = useEditable(quote)
  const taskGroups = useTaskGroups()
  const phasesEnabled = usePhasesEnabled()
  const reviewer = useHasPermission("quote:review")
  const { update, pending } = useQuoteUpdate(quote.id)
  const { review, pending: reviewing } = useReviewTasks(quote.id)
  const queued = tasks.filter((task) => inQueue(quote, task))
  const target = acceptedStatus(quote.disposition)
  const complete = tasks.filter(
    (task) =>
      isComplete(quote, task) &&
      !["excluded", "approved", target].includes(task.status),
  )
  const incomplete = tasks.filter((task) => !isComplete(quote, task)).length
  const excludable = tasks.filter(
    (task) => !["excluded", "approved", "rejected"].includes(task.status),
  )
  const excluded = tasks.filter((task) => task.status === "excluded")
  const decided = tasks.filter((task) =>
    ["approved", "rejected"].includes(task.status),
  )
  const skipped = [
    ...(excluded.length > 0 ? [`${excluded.length} already excluded`] : []),
    ...(decided.length > 0
      ? [`${decided.length} decided by the customer`]
      : []),
  ]
  const busy = pending || reviewing
  const setStatus = (
    targets: QuoteTaskDetailPublic[],
    status: QuoteTaskStatus,
  ) =>
    update(
      {
        tasks: targets.flatMap(
          (task) => underTask(task.id, { status }).tasks ?? [],
        ),
      },
      onClear,
    )

  return (
    <FloatingActionBar
      count={tasks.length}
      summary={formatTotals(tasks.flatMap(totalCharges)).join(" · ")}
      onClear={onClear}
    >
      {reviewer && queued.length > 0 && (
        <FloatingAction
          hint={`Approve ${queued.length}`}
          disabled={busy}
          onClick={() =>
            review(
              {
                task_ids: queued.map((task) => task.id),
                decision: "approved",
              },
              onClear,
            )
          }
        >
          <Check />
          Approve
        </FloatingAction>
      )}
      {editable && quote.disposition === "draft" && (
        <FloatingAction
          hint={
            incomplete > 0
              ? `${incomplete} of ${tasks.length} selected tasks aren't complete yet`
              : `Mark ${complete.length} ready`
          }
          disabled={busy || complete.length === 0}
          onClick={() => setStatus(complete, acceptedStatus(quote.disposition))}
        >
          <RadioButtonChecked />
          Mark ready
        </FloatingAction>
      )}
      {editable && phasesEnabled && quote.active_phases.length > 0 && (
        <PhasesMenu
          quote={quote}
          tasks={tasks}
          disabled={busy}
          onApply={(body) => update(body, onClear)}
        />
      )}
      {editable && !isPartsQuote(quote) && !travellerOf(quote) && (
        <ChoiceMenu
          label="Task group"
          noun="task groups"
          emptyLabel="No task group"
          choices={taskGroups}
          disabled={busy}
          onPick={(taskGroupId) =>
            update(
              acrossTasks(
                tasks.map((task) => task.id),
                { task_group_id: taskGroupId },
              ),
              onClear,
            )
          }
        />
      )}
      {editable && excludable.length + decided.length > 0 && (
        <FloatingAction
          hint={[
            `Exclude ${excludable.length}`,
            ...(skipped.length > 0 ? [`Skips ${skipped.join(" and ")}`] : []),
          ].join(" · ")}
          disabled={busy || excludable.length === 0}
          onClick={() => setStatus(excludable, "excluded")}
        >
          <DoNotDisturbOnOutline />
          Exclude
        </FloatingAction>
      )}
      {editable && excluded.length > 0 && (
        <FloatingAction
          hint={`Include ${excluded.length}`}
          disabled={busy}
          onClick={() => setStatus(excluded, includedStatus(quote.disposition))}
        >
          <AddCircleOutline />
          Include
        </FloatingAction>
      )}
    </FloatingActionBar>
  )
}

function PhasesMenu({
  quote,
  tasks,
  disabled,
  onApply,
}: {
  quote: QuoteDetailPublic
  tasks: QuoteTaskDetailPublic[]
  disabled: boolean
  onApply: (body: UpdateQuote) => void
}): React.ReactElement {
  const [open, setOpen] = useState(false)
  const shared = quote.active_phases
    .map((phase) => phase.id)
    .filter((phaseId) =>
      tasks.every((task) => task.phase_ids.includes(phaseId)),
    )

  const apply = (chosen: string[]) => {
    onApply({
      tasks: tasks.map((task) => ({
        operation: "update",
        id: task.id,
        phase_ids: rephased(task.phase_ids, shared, chosen),
      })),
    })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FloatingAction disabled={disabled}>
          <LayersOutline />
          Phase
          <ExpandMore />
        </FloatingAction>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 p-0">
        <MultiSelectBody
          options={quote.active_phases.map((phase) => ({
            value: phase.id,
            label: phase.name,
          }))}
          initial={shared}
          isLoading={false}
          searchPlaceholder="Search phases…"
          emptyLabel="No phases"
          onApply={apply}
        />
      </PopoverContent>
    </Popover>
  )
}

export function ChoiceMenu({
  label,
  noun,
  emptyLabel,
  choices,
  disabled,
  onPick,
}: {
  label: string
  noun: string
  emptyLabel: string
  choices: { id: string; name: string }[]
  disabled: boolean
  onPick: (id: string | null) => void
}): React.ReactElement {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const needle = search.trim().toLowerCase()
  const shown = choices.filter((choice) =>
    choice.name.toLowerCase().includes(needle),
  )

  const pick = (id: string | null) => {
    onPick(id)
    setOpen(false)
    setSearch("")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FloatingAction disabled={disabled}>
          <FolderOutline />
          {label}
          <ExpandMore />
        </FloatingAction>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-64 space-y-1 p-1">
        <Input
          autoFocus
          aria-label={`Search ${noun}`}
          placeholder={`Search ${noun}…`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="max-h-64 overflow-y-auto">
          <Button
            variant="ghost"
            className="w-full justify-start px-2 text-body-regular text-muted-foreground"
            onClick={() => pick(null)}
          >
            {emptyLabel}
          </Button>
          {shown.map((choice) => (
            <Button
              key={choice.id}
              variant="ghost"
              className="w-full justify-start px-2 text-body-regular"
              onClick={() => pick(choice.id)}
            >
              <span className="truncate">{choice.name}</span>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function ReviewRowActions({
  quote,
  task,
}: {
  quote: QuoteDetailPublic
  task: QuoteTaskDetailPublic
}): React.ReactElement {
  const { review, pending } = useReviewTasks(quote.id)

  return (
    <span className={ROW_ACTIONS}>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-semantic-success"
        aria-label={`Approve ${task.number}`}
        title="Approve"
        disabled={pending}
        onClick={() => review({ task_ids: [task.id], decision: "approved" })}
      >
        <Check />
      </Button>
      <Button
        asChild
        variant="ghost"
        size="icon-sm"
        aria-label={`Comment on ${task.number}`}
        title="Comment"
      >
        <Link
          to="/quotes/$quoteId/tasks/$taskId"
          params={{ quoteId: quote.id, taskId: task.id }}
          search={true}
        >
          <ChatBubbleOutline />
        </Link>
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground"
        aria-label={`Exclude ${task.number}`}
        title="Exclude"
        disabled={pending}
        onClick={() => review({ task_ids: [task.id], decision: "excluded" })}
      >
        <Block />
      </Button>
    </span>
  )
}
